import { ErroApi, SemRede, api, guardarSessao } from './api.js'

/// Login com a conta Ápia (Entra ID), por **redirecionamento**.
///
/// Era por popup, e no computador funcionava. No iPhone, não: o Safari abre o
/// popup como uma aba separada, o app perde o contato com ela, e a tentativa fica
/// "em andamento" para sempre — toda tentativa seguinte morre em
/// `interaction_in_progress`. No aplicativo instalado na tela inicial é pior
/// ainda, porque popup nem existe. Redirecionar funciona em qualquer navegador:
/// a página vai até a Microsoft e volta.
///
/// A volta chega na raiz do site (a URI de retorno registrada é a origem pura —
/// com caminho, o Entra recusa com AADSTS50011), com o código no fragmento da URL.
/// Ela precisa ser tratada **antes** de o roteador existir: o roteador mandaria
/// a raiz para /entrar e apagaria o fragmento antes de alguém lê-lo. Por isso
/// `voltandoDoLogin` é consultado no main.tsx, antes de qualquer rota.

const TENANT = import.meta.env.VITE_ENTRA_TENANT_ID as string | undefined
const CLIENTE = import.meta.env.VITE_ENTRA_CLIENT_ID as string | undefined
export const loginConfigurado = Boolean(TENANT && CLIENTE)

const CHAVE_ERRO = 'qsms.erroLogin'

/// O MSAL tem ~250 kB e só serve no login, uma vez a cada 30 dias. Carregar sob
/// demanda tira esse peso do arranque diário do app no 4G da obra.
async function carregarMsal() {
  const { PublicClientApplication } = await import('@azure/msal-browser')
  const msal = new PublicClientApplication({
    auth: {
      clientId: CLIENTE!,
      authority: `https://login.microsoftonline.com/${TENANT}`,
      redirectUri: window.location.origin,
      // A volta é tratada na própria raiz, no main.tsx. Deixar o MSAL navegar
      // de volta para /entrar abriria uma corrida com o roteador pelo fragmento.
      navigateToLoginRequestUrl: false,
    },
    cache: {
      // O token do Entra fica no localStorage para sobreviver ao fechamento do
      // app; o que realmente importa para o offline é o token de dispositivo,
      // que mora no IndexedDB.
      cacheLocation: 'localStorage',
      // O estado da ida (o `state` e o verificador PKCE) também. No sessionStorage
      // ele se perde quando o iPhone abre a Microsoft num contexto separado — no
      // aplicativo instalado, por exemplo — e a volta falha por "estado ausente".
      temporaryCacheLocation: 'localStorage',
      storeAuthStateInCookie: false,
    },
  })
  await msal.initialize()
  return msal
}

export function descreverDispositivo(): string {
  const ua = navigator.userAgent
  const plataforma = /android/i.test(ua) ? 'Android' : /iphone|ipad/i.test(ua) ? 'iOS' : 'Computador'
  const navegador = /edg/i.test(ua) ? 'Edge' : /chrome|crios/i.test(ua) ? 'Chrome' : /safari/i.test(ua) ? 'Safari' : 'Navegador'
  return `${plataforma} · ${navegador}`
}

/// A URL atual é a volta do Entra ID?
export function voltandoDoLogin(): boolean {
  const h = window.location.hash
  return /[#&](code|error)=/.test(h) && /[#&]state=/.test(h)
}

/// Uma tentativa que morreu no meio (aba fechada, popup do sistema antigo)
/// deixa o MSAL achando que ainda há login em andamento, e ele recusa começar
/// outro. Como esta função só roda num toque do usuário no botão, qualquer
/// "em andamento" que exista é resto, e pode ser descartado.
function descartarInteracaoPresa() {
  for (const armazenamento of [window.sessionStorage, window.localStorage]) {
    try {
      for (const chave of Object.keys(armazenamento)) {
        if (/interaction\.status/i.test(chave)) armazenamento.removeItem(chave)
      }
    } catch {
      // Armazenamento bloqueado (navegação privada antiga): nada a limpar.
    }
  }
}

/// Sai para a Microsoft. Não volta: a página é substituída.
export async function iniciarLogin(): Promise<void> {
  if (!window.isSecureContext || !window.crypto?.subtle) throw new ContextoInseguro()
  descartarInteracaoPresa()
  const msal = await carregarMsal()
  await msal.loginRedirect({
    scopes: ['openid', 'profile', 'email'],
    prompt: 'select_account',
  })
}

/// Trata a volta: troca o que a Microsoft devolveu pela sessão de 30 dias.
export async function concluirLogin(): Promise<void> {
  const msal = await carregarMsal()
  const resultado = await msal.handleRedirectPromise()
  if (!resultado) throw new Error('A volta do login não trouxe resposta. Tente entrar de novo.')

  // O token que vai para a API é o **de acesso** do escopo `access_as_user`,
  // emitido para ela — é o caminho correto.
  //
  // Ele é pedido em silêncio, depois do login, e não no próprio redirecionamento:
  // se o escopo tiver qualquer detalhe errado no registro (URI do aplicativo,
  // consentimento), pedi-lo junto derrubaria o login inteiro. Em silêncio, a
  // falha só faz cair para o token de identidade, que a API também aceita — a
  // audiência dele é o próprio aplicativo.
  let token = resultado.idToken
  try {
    const acesso = await msal.acquireTokenSilent({
      scopes: [`api://${CLIENTE}/access_as_user`],
      account: resultado.account,
    })
    if (acesso.accessToken) token = acesso.accessToken
  } catch (e) {
    console.warn('[login] token de acesso indisponível; usando o de identidade', e)
  }
  if (!token) throw new Error('O login não devolveu token.')

  const sessao = await api.entrar(token, descreverDispositivo())
  await guardarSessao(sessao)
}

/// Token da Microsoft Graph para as telas de administração — hoje, só a busca de
/// pessoas no diretório, ao cadastrar um usuário.
///
/// Primeiro em silêncio, com o que o login deixou guardado. Quando isso não basta
/// (a sessão da Microsoft venceu, ou falta consentimento para a permissão), só com
/// `interativo`, que abre uma janela: tem de nascer de um clique, senão o
/// navegador bloqueia. É popup, e não redirecionamento, porque sair da página
/// perderia o formulário preenchido — e esta tela é usada no computador, onde
/// popup funciona (o problema era o iPhone, e ele não chega aqui).
export async function tokenGraph(escopos: string[], interativo: boolean): Promise<string> {
  if (!loginConfigurado) throw new Error('O login da Microsoft não está configurado neste app.')
  const msal = await carregarMsal()
  const conta = msal.getAllAccounts()[0]
  if (!interativo) {
    if (!conta) throw new PrecisaConectar()
    try {
      return (await msal.acquireTokenSilent({ scopes: escopos, account: conta })).accessToken
    } catch {
      throw new PrecisaConectar()
    }
  }
  descartarInteracaoPresa()
  const r = await msal.acquireTokenPopup({ scopes: escopos, ...(conta ? { account: conta } : {}) })
  return r.accessToken
}

/// O token em silêncio não saiu: é preciso um clique para abrir a janela da Microsoft.
export class PrecisaConectar extends Error {}

/// Esta janela é o popup do `tokenGraph` voltando da Microsoft?
///
/// Ele volta para a raiz do site, como o login, e com o mesmo fragmento na URL.
/// Quem lê o fragmento é a janela que abriu o popup; se esta aqui o tratasse como
/// fim de login, apagaria o código antes e criaria uma sessão nova à toa.
export function ehPopupDaMicrosoft(): boolean {
  return voltandoDoLogin() && Boolean(window.opener) && window.opener !== window
}

/// A volta falhou: a mensagem precisa atravessar o recarregamento até a tela de
/// login, que é quem a mostra.
export function guardarErroDeLogin(e: unknown) {
  try {
    window.sessionStorage.setItem(CHAVE_ERRO, traduzirErroDeLogin(e))
  } catch {
    // Sem armazenamento, a tela de login aparece sem a explicação.
  }
}

export function lerErroDeLogin(): string | null {
  try {
    const m = window.sessionStorage.getItem(CHAVE_ERRO)
    if (m) window.sessionStorage.removeItem(CHAVE_ERRO)
    return m
  } catch {
    return null
  }
}

/// Endereço sem HTTPS e fora de localhost: o navegador desliga a criptografia que
/// o login precisa. Não é defeito do sistema nem do servidor.
export class ContextoInseguro extends Error {}

export function traduzirErroDeLogin(e: unknown): string {
  if (e instanceof ContextoInseguro) {
    return (
      `Este endereço (${window.location.host}) não permite login: o navegador só libera ` +
      'a criptografia necessária em HTTPS ou em localhost. Use https://qsms.grupoapia.com.br.'
    )
  }
  if (e instanceof SemRede) {
    return 'Sem conexão com o servidor. O login precisa de internet — tente de novo quando tiver sinal.'
  }
  if (e instanceof ErroApi) return e.message
  const mensagem = (e as Error)?.message ?? ''
  if (mensagem.includes('crypto_nonexistent')) {
    return (
      `Este endereço (${window.location.host}) não permite login: o navegador só libera ` +
      'a criptografia necessária em HTTPS ou em localhost.'
    )
  }
  if (/user_cancelled|access_denied/.test(mensagem)) return 'Login cancelado.'
  if (mensagem.includes('interaction_in_progress')) {
    return 'Havia uma tentativa de login presa. Toque em "Entrar" de novo.'
  }
  if (mensagem.includes('no_tokens_found') || mensagem.includes('state_not_found')) {
    return 'O login não voltou completo. Toque em "Entrar" de novo.'
  }
  return mensagem || 'Não foi possível entrar.'
}
